import React, { useState, useEffect, useRef } from 'react';
import { Modal, Box, IconButton, Backdrop, Grid, CircularProgress, Typography } from '@mui/material';
import { CloseIcon } from 'icons/materialIcons';
import EventBadgesLeft from './EventBadgesLeft';
import EventBadgesRight from './EventBadgesRight';
import { getCardById } from 'apis/cardApi';
import { useParams } from 'react-router-dom';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const EventBadgesModal = ({ open, onClose }) => {
    const { cardId } = useParams();
    const [selectedOption, setSelectedOption] = useState('badge');
    const [cardDetails, setCardDetails] = useState({});
    const [loading, setLoading] = useState(true);
    const [callToActionText, setCallToActionText] = useState('');
    const [downloadContent, setDownloadContent] = useState('');
    const contentRef = useRef(null);
    const [isDownloading, setIsDownloading] = useState(false);
    const [isPrinting, setIsPrinting] = useState(false);
    const [downloadProgress, setDownloadProgress] = useState(0);
    const canvasRef = useRef(null);

    const [checkedItems, setCheckedItems] = useState({
        profilePhoto: true,
        jobTitle: true,
        location: true,
        callToAction: true,
        name: true,
        company: true,
        qrCode: true,
        qrCodeLogo: true,
    });

    // Reset canvas cache when checked items or layout changes
    useEffect(() => {
        canvasRef.current = null;
    }, [checkedItems, selectedOption, callToActionText]);

    const captureContent = async () => {
        if (!contentRef.current) return null;

        try {
            setDownloadProgress(25);

            // Using html2canvas directly on the content
            const canvas = await html2canvas(contentRef.current, {
                scale: 2,
                useCORS: true,
                allowTaint: true,
                backgroundColor: 'transparent', // Transparent background to preserve only the original border
                logging: false,
                imageTimeout: 0
            });

            setDownloadProgress(75);

            return canvas;
        } catch (error) {
            console.error("Error capturing content:", error);
            return null;
        }
    };

    const handleDownload = async (format) => {
        if (!contentRef.current || isDownloading) return;

        setIsDownloading(true);
        setDownloadProgress(5);

        try {
            const fileName = `event-badge-${cardId}-${Date.now()}`;

            // Capture the content
            const canvas = await captureContent();
            if (!canvas) {
                throw new Error("Failed to capture badge content");
            }

            setDownloadProgress(60);

            // Get dimensions
            const imgWidth = canvas.width;
            const imgHeight = canvas.height;
            const margin = 30; // Margin around the badge

            // Create a new canvas with margins
            const tempCanvas = document.createElement('canvas');
            tempCanvas.width = imgWidth + (margin * 2);
            tempCanvas.height = imgHeight + (margin * 2);
            const ctx = tempCanvas.getContext('2d');

            // Fill with white background
            ctx.fillStyle = 'white';
            ctx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);

            // Draw the captured badge image (which already has rounded corners)
            ctx.drawImage(canvas, margin, margin, imgWidth, imgHeight);

            setDownloadProgress(85);

            if (format === 'pdf') {
                // Create PDF
                const pdf = new jsPDF({
                    orientation: tempCanvas.width > tempCanvas.height ? 'landscape' : 'portrait',
                    unit: 'px',
                    format: [tempCanvas.width, tempCanvas.height]
                });

                // Add the image to PDF
                pdf.addImage(tempCanvas.toDataURL('image/png'), 'PNG', 0, 0, tempCanvas.width, tempCanvas.height);
                pdf.save(`${fileName}.pdf`);
            } else {
                // PNG download
                const link = document.createElement('a');
                link.download = `${fileName}.png`;
                link.href = tempCanvas.toDataURL('image/png');
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }

            setDownloadProgress(100);
        } catch (error) {
            console.error("Download error:", error);
            alert("There was an error downloading your badge. Please try again.");
        } finally {
            setTimeout(() => {
                setIsDownloading(false);
                setDownloadProgress(0);
            }, 300);
        }
    };

    const handlePrint = async () => {
        if (!contentRef.current || isPrinting) return;

        setIsPrinting(true);
        setDownloadProgress(5);

        try {
            // Capture the content
            const canvas = await captureContent();
            if (!canvas) {
                throw new Error("Failed to capture badge content");
            }

            // Get dimensions
            const imgWidth = canvas.width;
            const imgHeight = canvas.height;
            const margin = 30;

            // Create a new canvas with margins
            const tempCanvas = document.createElement('canvas');
            tempCanvas.width = imgWidth + (margin * 2);
            tempCanvas.height = imgHeight + (margin * 2);
            const ctx = tempCanvas.getContext('2d');

            // Fill with white background
            ctx.fillStyle = 'white';
            ctx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);

            // Draw the captured badge image
            ctx.drawImage(canvas, margin, margin, imgWidth, imgHeight);

            setDownloadProgress(80);

            // Convert to image for print
            const imgData = tempCanvas.toDataURL('image/png');

            // Create a new window for printing
            const printWindow = window.open('', '_blank');
            if (!printWindow) {
                alert('Please allow popups to print the badge');
                setIsPrinting(false);
                setDownloadProgress(0);
                return;
            }

            // Create the HTML content for the print window
            const htmlContent = `
                <!DOCTYPE html>
                <html>
                <head>
                    <title>Print Event Badge</title>
                    <style>
                        @page {
                            size: auto;
                            margin: 0.5cm;
                        }
                        html, body {
                            margin: 0;
                            padding: 0;
                            height: 100%;
                            overflow: hidden;
                            background-color: white;
                        }
                        body {
                            display: flex;
                            justify-content: center;
                            align-items: center;
                            min-height: 100vh;
                            page-break-after: avoid;
                            page-break-inside: avoid;
                        }
                        .print-container {
                            display: flex;
                            flex-direction: column;
                            align-items: center;
                            max-width: 100%;
                            page-break-inside: avoid;
                        }
                        .print-title {
                            font-size: 16px;
                            font-weight: bold;
                            margin-bottom: 15px;
                            color: #333;
                            text-align: center;
                        }
                        .badge-container {
                            display: flex;
                            justify-content: center;
                            align-items: center;
                            padding: 5px;
                            page-break-inside: avoid;
                        }
                        .badge-image {
                            max-width: 100%;
                            height: auto;
                            display: block;
                        }
                        .print-button {
                            margin-top: 15px;
                            padding: 8px 16px;
                            background-color: #000;
                            color: white;
                            border: none;
                            border-radius: 20px;
                            cursor: pointer;
                        }
                        @media print {
                            .no-print {
                                display: none !important;
                            }
                            html, body {
                                height: auto;
                                overflow: visible;
                            }
                        }
                    </style>
                </head>
                <body>
                    <div class="print-container">
                        <div class="print-title no-print">Event Badge Preview</div>
                        <div class="badge-container">
                            <img src="${imgData}" class="badge-image" alt="Event Badge">
                        </div>
                        <button class="print-button no-print" onclick="window.print(); return false;">
                            Print Badge
                        </button>
                    </div>
                    <script>
                        // Auto print after a delay to ensure everything is loaded
                        setTimeout(function() {
                            window.print();
                        }, 1000);
                    </script>
                </body>
                </html>
            `;

            printWindow.document.write(htmlContent);
            printWindow.document.close();

            // Handle print window close
            printWindow.onafterprint = () => {
                setIsPrinting(false);
                setDownloadProgress(0);
            };

            // Fallback in case onafterprint doesn't fire
            setTimeout(() => {
                setIsPrinting(false);
                setDownloadProgress(0);
            }, 5000);

        } catch (error) {
            console.error("Print error:", error);
            alert("There was an error preparing the badge for printing. Please try again.");
            setIsPrinting(false);
            setDownloadProgress(0);
        }
    };

    // Fetch card details when the modal opens
    useEffect(() => {
        const fetchCardDetails = async () => {
            setLoading(true);
            try {
                const data = await getCardById(cardId);
                setCardDetails(data);
            } catch (error) {
                console.error('Error fetching card details:', error);
            } finally {
                setLoading(false);
            }
        };

        if (cardId) {
            fetchCardDetails();
        }
    }, [cardId]);

    // Handle changing the selected layout
    const handleSelectionChange = (option) => {
        setSelectedOption(option);
    };

    // Handle toggling the checkboxes
    const handleCheckboxChange = (event) => {
        setCheckedItems((prevCheckedItems) => ({
            ...prevCheckedItems,
            [event.target.name]: event.target.checked,
        }));
    };

    // Pass the call-to-action text
    const handleCallToActionChange = (text) => {
        setCallToActionText(text);
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            aria-labelledby="modal-title"
            aria-describedby="modal-description"
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{
                sx: {
                    backgroundColor: 'rgba(194, 194, 194, 0.7)',
                    backdropFilter: 'blur(8px)',
                },
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '80vw',
                    height: '100vh',
                    bgcolor: "#fff",
                    borderRadius: "20px",
                    boxShadow: 24,
                    paddingLeft: 4,
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                }}
            >
                <IconButton
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        top: 10,
                        right: 16,
                        zIndex: 10
                    }}
                >
                    <CloseIcon />
                </IconButton>

                {(isDownloading || isPrinting) && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'rgba(255, 255, 255, 0.8)',
                            zIndex: 1000,
                            borderRadius: '20px',
                        }}
                    >
                        <Box sx={{ textAlign: 'center', width: '250px' }}>
                            <CircularProgress
                                variant="determinate"
                                value={downloadProgress}
                                size={60}
                                thickness={4}
                                sx={{ mb: 2 }}
                            />
                            <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                                {isPrinting ?
                                    (downloadProgress < 30 ? 'Capturing badge content...' :
                                        downloadProgress < 60 ? 'Preparing badge for print...' :
                                            downloadProgress < 80 ? 'Creating print preview...' :
                                                'Opening print dialog...') :
                                    (downloadProgress < 30 ? 'Capturing badge content...' :
                                        downloadProgress < 60 ? 'Preparing badge...' :
                                            downloadProgress < 80 ? 'Creating image...' :
                                                'Finalizing download...')
                                }
                            </Typography>
                        </Box>
                    </Box>
                )}

                <Grid container spacing={2} sx={{ flexGrow: 1, mt: 4 }}>
                    <Grid item xs={12} md={5} sx={{ display: 'flex', flexDirection: 'column' }}>
                        <EventBadgesLeft
                            selectedOption={selectedOption}
                            onSelectionChange={handleSelectionChange}
                            checkedItems={checkedItems}
                            onCheckboxChange={handleCheckboxChange}
                            callToActionText={callToActionText}
                            onCallToActionChange={handleCallToActionChange}
                            onDownload={handleDownload}
                            onPrint={handlePrint}
                        />
                    </Grid>

                    <Grid item xs={12} md={7} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                        <EventBadgesRight
                            loading={loading}
                            cardDetails={cardDetails}
                            checkedItems={checkedItems}
                            callToActionText={callToActionText}
                            layout={selectedOption}
                            setDownloadContent={setDownloadContent}
                            contentRef={contentRef}
                        />
                    </Grid>
                </Grid>
            </Box>
        </Modal>
    );
};

export default EventBadgesModal;